import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { PaymentTypes } from '@appTypes/enums';
import { resetOnLogoutReducer } from '@utils';

import {
  PAYMENT_LOG_TABS,
  defaultPastMember,
  initialSliceState,
} from '../constants';
import {
  PastMemberPaymentLog,
  PaymentLogRosterFilters,
  PaymentLogUnitFilters,
  QueriedLogs,
  QueriedLogsFilters,
  SelectedEditPaymentLog,
} from '../types';

export const paymentLogsSlice = createSlice({
  name: 'paymentLogs',
  initialState: initialSliceState,

  reducers: {
    setUserGuid: (state, action: PayloadAction<{ personGuid: string }>) => {
      state.userGuid = action.payload.personGuid;
      state.pastMember = defaultPastMember;
    },
    setPastMember: (
      state,
      action: PayloadAction<{ pastMemberInfo: PastMemberPaymentLog }>,
    ) => {
      state.pastMember = action.payload.pastMemberInfo;
    },
    setSelectedEditPaymentLog: (
      state,
      action: PayloadAction<{ editLog: SelectedEditPaymentLog }>,
    ) => {
      state.selectedEditLog = action.payload.editLog;
    },
    setSearchFilter: (
      state,
      action: PayloadAction<{
        searchTerm: string;
      }>,
    ) => {
      const { searchTerm } = action.payload;
      state.searchFilter = searchTerm;
    },
    setFilter: (state, action: PayloadAction<PaymentLogRosterFilters>) => {
      state.filters = action.payload;
    },
    setUnitFilter: (state, action: PayloadAction<PaymentLogUnitFilters>) => {
      state.unitFilters = action.payload;
    },
    setSorter: (
      state,
      action: PayloadAction<{
        columnKey: string;
        order: 'ascend' | 'descend';
      }>,
    ) => {
      const { columnKey, order } = action.payload;
      state.sorter.columnKey = columnKey;
      state.sorter.order = order;
    },
    setSelectedRowKeys: (
      state,
      action: PayloadAction<{
        items: number[];
      }>,
    ) => {
      const { items } = action.payload;
      state.selectedRowKeys = items;
    },
    setPaymentLogsActiveTab: (
      state,
      action: PayloadAction<{
        activeTab: PAYMENT_LOG_TABS;
      }>,
    ) => {
      const { activeTab } = action.payload;
      state.paymentLogsActiveTab = activeTab;
    },
    setQueriedLogs: (state, action: PayloadAction<Partial<QueriedLogs>>) => {
      const updatedQueriedLogs = Object.keys(state.queriedLogs).reduce(
        (curr, payType) => {
          const key = payType as PaymentTypes;
          const newLogResult = action.payload[key];
          if (newLogResult) {
            return { ...curr, [key]: newLogResult };
          }
          return { ...curr, [key]: [] };
        },
        { ...state.queriedLogs },
      );
      state.queriedLogs = updatedQueriedLogs;
    },
    setQueriedLogsFilters: (
      state,
      action: PayloadAction<Partial<QueriedLogsFilters>>,
    ) => {
      state.queriedLogsFilters = {
        ...state.queriedLogsFilters,
        ...action.payload,
      };
    },
  },
});

const { reducer: sliceReducer, actions } = paymentLogsSlice;

export const {
  setSearchFilter,
  setFilter,
  setUnitFilter,
  setUserGuid,
  setPastMember,
  setSelectedEditPaymentLog,
  setSorter,
  setSelectedRowKeys,
  setPaymentLogsActiveTab,
  setQueriedLogs,
  setQueriedLogsFilters,
} = actions;

export default resetOnLogoutReducer(sliceReducer);
